import { ScrollSide } from '@/webgl/runner/entities/BackgroundEntity'
import { BonusType } from '@/webgl/runner/entities/BonusEntity'
import { CollectibleType } from '@/webgl/runner/entities/CollectibleEntity'
import { SequenceViewVariant } from '@/webgl/runner/scenarios/SequenceConfig.types'

import { ScenarioSetup } from '../ScenarioSetup.types'

const { Standard, Obstacle, TurningPoint, Bonus } = CollectibleType

const config: ScenarioSetup = {
  backgroundColor: 0xfed1c8,
  lanesColor: 0xe2b577,
  lanesExtraColor: 0xfed1c8,
  lanesTextureId: 'wood',

  clearColor: 0xfff4e5,
  smokeColor: 0xf7ead8,

  playerColor: 0xfed1c8,
  playerGoodColor: 0xfce5ab,
  playerWrongColor: 0xfe8769,

  //#region audio
  audioSfx: { path: 'sfx_audio/sfx.json' },
  audio: {
    en: {
      path: 'pedro_1/audios/en/bundle_1.json',
      subtitles: 'pedro_1/audios/en/bundle_1.vtt',
    },
  },
  //#endregion

  //#region collectibles
  collectibles: {
    resources: [{ request: 'pedro_1/collectible.glb' }],

    items: [
      {
        id: 'towel',
        type: Standard,
      },
      {
        id: 'soap',
        type: Standard,
      },
      {
        id: 'comb',
        type: Standard,
      },
      {
        id: 'underwear',
        type: Standard,
      },
      {
        id: 'jean',
        type: Standard,
      },
      {
        id: 'socks_pair',
        type: Standard,
      },
      {
        id: 'sock_mono',
        type: Standard,
      },
      {
        id: 'sock_dots',
        type: Standard,
      },
      {
        id: 'sock_flower',
        type: Standard,
      },
      {
        id: 'sock_doublelines',
        type: Standard,
      },
      {
        id: 'bowl_empty',
        type: Standard,
      },
      {
        id: 'milk',
        type: Standard,
      },
      {
        id: 'cereal',
        type: Standard,
      },
      {
        id: 'bowl_full',
        type: Standard,
      },
      {
        id: 'backpack',
        type: Standard,
      },
      {
        id: 'dog',
        type: TurningPoint,
      },
      {
        id: 'squirrel',
        type: TurningPoint,
      },
      {
        id: 'television',
        type: TurningPoint,
        audio: 'p1_distract_television',
      },
      {
        id: 'stretching',
        type: Bonus,
        bonus: BonusType.Relax,
        audio: 'p1_that_feels_great_A',
      },
    ],
  },
  //#endregion

  //#region particles
  particles: {
    resources: [{ id: 'default', request: 'common/particles.glb' }],
  },
  //#endregion

  //#region background
  background: {
    resources: [
      {
        id: 'background',
        request: 'pedro_1/background.glb',
      },
      {
        id: 'toaster',
        request: 'pedro_1/toaster.glb',
        animation: { framerate: 12, loop: false },
      },
      {
        id: 'headphones',
        request: 'pedro_1/headphones.glb',
        animation: { framerate: 12, loop: true },
      },
      {
        id: 'plant',
        request: 'pedro_1/plant.glb',
        animation: { framerate: 12, loop: true },
      },
    ],

    items: [
      { id: 'cream', side: ScrollSide.LEFT },
      { id: 'pot_brush', side: ScrollSide.RIGHT },
      { id: 'tube' },
      { id: 'cap', scale: 0.75 },
      { id: 'apple_full' },
      { id: 'apple_half' },
      { id: 'mug_1' },
      { id: 'mug_2' },
      { id: 'bowl' },
      { id: 'cereal' },
      { id: 'milk' },
      { id: 'cereal_turned' },
      { id: 'bowl_turned' },
      { id: 'mug_turned_1' },
      { id: 'mug_turned_2' },
      { id: 'headphones' },
      { id: 'toaster' },
      { id: 'plant' },
      { id: 'splash_1', scale: 0.7 },
      { id: 'splash_2', scale: 0.7 },
    ],
  },
  //#endregion

  sequences: [
    //#region bathroom
    {
      id: 'bathroom',

      view: { variant: SequenceViewVariant.Default },

      stop: {
        alterJauge: 0.1,
      },

      goals: [['towel', 'soap', 'comb']],
      goalText: 'Collect items in this order:',
      track: {
        type: 'procedural',
        collectibles: ['towel', 'soap', 'comb'],
      },

      scrolling: ['cream', 'pot_brush', 'tube', 'plant'],
    },
    //#endregion

    //#region dressed
    {
      id: 'dressed_1',

      view: { variant: SequenceViewVariant.Default },
      condition: { id: 'collected-count', count: 1 },

      goals: [['underwear', 'jean', 'socks_pair']],
      goalText: 'Collect items in this order:',

      track: {
        type: 'procedural',
        warmupTime: 0.0,
        collectibles: ['underwear', 'socks_pair'],
      },

      scrolling: ['cap', 'headphones', 'plant'],
    },

    {
      id: 'dressed_2',

      view: { variant: SequenceViewVariant.Default },

      goals: [['underwear', 'jean', 'socks_pair']],
      goalText: 'Collect items in this order:',

      collected: [['underwear']],

      track: {
        type: 'procedural',
        collectibles: [
          'underwear',
          'jean',
          'socks_pair',
          // "stretching",
        ],
        barriers: ['television'],
      },

      scrolling: ['cap', 'headphones', 'plant'],
    },
    {
      id: 'dressed_3',

      view: { variant: SequenceViewVariant.Default },
      condition: { id: 'collected-count', count: 2 },

      goals: [['underwear', 'jean', 'socks_pair']],
      goalText: 'Collect items in this order:',

      collected: [['underwear']],

      track: {
        type: 'procedural',
        collectibles: [
          'underwear',
          'jean',
          'socks_pair',
          // "stretching",
        ],
        // barriers: [
        //   "stretching"
        // ]
      },

      scrolling: ['cap', 'headphones', 'plant'],
    },
    {
      id: 'dressed_4',

      view: { variant: SequenceViewVariant.Default },

      goals: [['underwear', 'jean', 'socks_pair']],
      goalText: 'Collect items in this order:',

      collected: [['underwear', 'jean']],

      track: {
        type: 'procedural',
        collectibles: [
          'underwear',
          'jean',
          'socks_pair',
          // "stretching",
        ],
        barriers: ['stretching'],
      },

      scrolling: ['cap', 'headphones', 'plant'],
    },
    //#endregion

    //#region dressed_socks
    {
      id: 'dressed_socks',

      audio: {
        wrong: ['p1_socks_rand_1'],
      },

      condition: { id: 'spawned-count', count: 30 },
      resolution: { id: 'barrier', barrier: 'dog' },

      view: { variant: SequenceViewVariant.Default },
      jaugeRate: 0.05,

      goals: [['underwear', 'jean', 'sock_doublelines']],
      goalText: 'Collect items in this order:',

      collected: [['underwear', 'jean']],

      track: {
        type: 'procedural',
        rate: 0.75,
        variation: 0.5,
        collectibles: [
          // "sock_mono",
          'sock_dots',
          // "sock_flower",
          // "sock_doublelines"
        ],
      },

      scrolling: ['cap', 'headphones', 'plant'],
    },
    //#endregion

    //#region breakfast
    {
      id: 'breakfast',

      condition: { id: 'collected-count', count: 2 },

      // start: {
      // alterJauge: -1
      // },

      stop: {
        preserveCollectibles: true,
      },

      view: { variant: SequenceViewVariant.Default },

      goals: [['bowl_empty', 'milk', 'cereal', 'bowl_full']],
      goalText: 'Collect items in this order:',

      track: {
        type: 'procedural',
        collectibles: ['bowl_empty', 'milk', 'cereal', 'bowl_full'],
      },

      scrolling: ['apple_full', 'mug_1', 'mug_2', 'pot', 'milk', 'toaster', 'bowl', 'cereal'],
    },
    //#endregion

    //#region breakfast_milk
    {
      id: 'breakfast_milk',

      condition: { id: 'spawned-count', count: 50 },

      // resolution: { id: "barrier", barrier: "squirrel" },

      view: { variant: SequenceViewVariant.Default },

      goals: [['bowl_empty', 'milk', 'cereal', 'bowl_full']],
      goalText: 'Collect items in this order:',

      collected: [['bowl_empty', 'milk']],

      track: {
        type: 'procedural',
        rate: 1.2,
        variation: 0.2,
        collectibles: ['bowl_empty', 'milk', 'cereal'],
      },

      scrolling: ['apple_half', 'cereal_turned', 'bowl_turned', 'mug_turned_1', 'mug_turned_2', 'splash_1', 'splash_2'],
    },
    //#endregion

    //#region breakfast_end
    {
      id: 'breakfast_end',

      view: { variant: SequenceViewVariant.Default },

      goals: [['backpack']],

      track: {
        type: 'procedural',
        collectibles: ['backpack'],
      },

      scrolling: ['apple_full', 'mug_1', 'mug_2', 'pot', 'milk', 'toaster', 'bowl', 'cereal'],
    },
    //#endregion
  ],

  //#region Turning Points
  turningPoints: [
    {
      id: 'sock',
      duration: 3,
      fillAmount: 1.0,
      audio: ['p1_socks_turningpoint'],
    },
    {
      id: 'television',
      duration: 3,
      fillAmount: 0.8,
      audio: ['p1_distract_television'],
    },
    {
      id: 'dog',
      duration: 3,
      decreaseAmount: 1,
      audio: ['p1_dog_turningpoint_A', 'p1_mother_turningpoint'],
    },

    // {
    //   id: "squirrel",
    //   duration: 1.5,
    //   decreaseAmount: 1,
    //   audio: ["p1_squirrel_turningpoint_A", "p1_mother_help"]
    // }
  ],
  //#endregion

  //#region Audio Segments
  audioSegments: [
    {
      id: 'audio_intro',
      audios: ['p1_intro', 'p1_intro_game'],
    },

    {
      id: 'audio_mother_intro',
      audios: ['p1_intro_mother'],
    },

    {
      id: 'audio_dress_transition',
      audios: ['p1_dress_transition'],
    },

    {
      id: 'audio_mother_help',
      audios: ['p1_mother_help'],
    },

    {
      id: 'audio_outro',
      audios: ['p1_outro_okay', 'p1_outro-ohgosh'],
    },
  ],
  //#endregion
}

export default config
